import focusLock from 'dom-focus-lock';
import { AccessibilityElement } from './AccessibilityElement';
import './AccessibilityStyles.scss';

class AccessibilityPlugin extends Phaser.Plugins.BasePlugin {
  constructor(pluginManager) {
    super(pluginManager);
  }

  init() {
    // create parent dom to comtain accessible nodes
    this._accessibilityWrap = document.createElement('div');
    this._accessibilityWrap.setAttribute('id', 'jgg-accessibility');
    this._accessibilityWrap.style.position = 'absolute';
    this._accessibilityWrap.style.pointerEvents = 'none';
    this._accessibilityWrap.style.touchAction = 'none';

    // listen for focus and click events
    this._bindedFocus = this._handeElementFocus.bind(this);
    this._bindedBlur = this._handleElementBlur.bind(this);
    this._bindedSelect = this._handleElementSelect.bind(this);

    this._focusLocked = false;
    this._debug = false;
    this._currentFocus = null;
    this._items = [];
    this._tabIndex = 1;
    this._scale = 1;

    // enable by default
    this.enable();
  }

  set debug(value) {
    this._debug = value;
    if (value) {
      this._accessibilityWrap.classList.add('debug');
    } else {
      this._accessibilityWrap.classList.remove('debug');
    }
  }

  // =============================================================================================
  // ENABLE / DISABLE
  // =============================================================================================

  enable() {
    // listen for focus/click events
    this._accessibilityWrap.addEventListener('focusin', this._bindedFocus);
    this._accessibilityWrap.addEventListener('focusout', this._bindedBlur);
    this._accessibilityWrap.addEventListener('click', this._bindedSelect);
    this.game.scale.parent.appendChild(this._accessibilityWrap);
    // listen for game scale changes
    this.game.scale.on('resize', this._updateScale, this);
    this._updateScale();
  }

  disable() {
    // clear listeners
    this._accessibilityWrap.removeEventListener('focusin', this._bindedFocus);
    this._accessibilityWrap.removeEventListener('focusout', this._bindedBlur);
    this._accessibilityWrap.removeEventListener('click', this._bindedSelect);
    this.game.scale.off('resize', this._updateScale, this);
    // remove main wrapper from dom
    this.game.scale.parent.removeChild(this._accessibilityWrap);
  }

  // =============================================================================================
  // ADD / REMOVE ITEMS
  // =============================================================================================

  addItem(id, ariaLabel, rect) {
    // create and return the element object
    this._log('add item', { id, ariaLabel });
    rect.scale = this._scale;
    const item = new AccessibilityElement(this, this._accessibilityWrap, ariaLabel, rect);
    item.on('update', () => {
      this._debounceReindexElements();
    });
    this._items.push(item);
    this._debounceReindexElements();
    return item;
  }

  _debounceReindexElements() {
    if (this._debounceReindexElementsTimeout) return;
    this._debounceReindexElementsTimeout = setTimeout(() => {
      this._debounceReindexElementsTimeout = null;
      this.reindexElements();
    }, 100);
  }

  reindexElements() {
    this._tabIndex = 1;
    this._items.sort((a, b) => {
      if (Math.abs(a._y - b._y) < 20) {
        return a._x - b._x;
      }
      return a._y - b._y;
    });
    this._items.forEach((item) => {
      item._tabIndex = this._tabIndex++;
      item.reindexElement();
    });
    if (this._currentFocus) {
      this._currentFocus.focus();
    }
  }

  itemRemoved(item) {
    if (this._currentFocus == item) {
      this._currentFocus = null;
    }
    this._items = this._items.filter((i) => i !== item);
    item.off('update');
  }

  removeItem(item) {
    // just trigger destroy on the item, it will call itemRemoved
    item.destroy();
  }

  // =============================================================================================
  // INPUT EVENTS
  // =============================================================================================

  _handeElementFocus(e) {
    // trigger focus lock
    focusLock.on(this._accessibilityWrap);
    e.target?.controller?.focus();
    e.preventDefault();
    e.stopPropagation();
    if (e.target?.controller) {
      this._currentFocus = e.target.controller;
    }
  }

  _handleElementBlur(e) {
    if (this._currentFocus == e.target?.controller) {
      this._currentFocus = null;
    }
    e.target?.controller?.blur();
    e.preventDefault();
    e.stopPropagation();
  }

  _handleElementSelect(e) {
    e.target?.controller?.select();
    e.preventDefault();
    e.stopPropagation();
  }

  // =============================================================================================
  // SCALE
  // =============================================================================================

  _updateScale() {
    this._accessibilityWrap.style.top = this.game.scale.canvas.offsetTop + 'px';
    this._accessibilityWrap.style.left = this.game.scale.canvas.offsetLeft + 'px';
    this._accessibilityWrap.style.width = this.game.scale.displaySize.width + 'px';
    this._accessibilityWrap.style.height = this.game.scale.displaySize.height + 'px';
    this._accessibilityWrap.style.transform = getComputedStyle(this.game.scale.canvas).transform;

    const scale = this.game.scale.displaySize.height / this.game.scale.canvas.height;
    if (scale != this._scale) {
      this._scale = scale;
      this._items.forEach((item) => {
        item.scale = scale;
      });
    }
  }

  // =============================================================================================
  // LOGS
  // =============================================================================================

  _log(...args) {
    this._debug && console.log('%c[AccessibilityPlugin]', 'color:#00ff00', ...args);
  }

  _warn(...args) {
    this._debug && console.warn('%c[AccessibilityPlugin]', 'color:#00ff00', ...args);
  }

  // =============================================================================================
  // SHITDOWN
  // =============================================================================================

  destroy() {
    this.disable();
    super.destroy();
  }
}

export { AccessibilityPlugin };
